import React, { ChangeEvent, SyntheticEvent, useEffect, useState } from "react";
import { ChoiceList, Choice } from "@cmsgov/ds-medicare-gov";
import { displayDrug, useTranslate } from "../helpers";
import { PrescriptionDrug } from "../@types";

interface PDESelectorProps {
  savedPdes: PrescriptionDrug[];
  unsavedPdes: PrescriptionDrug[];
  onChoiceUpdated?: (updatedChoices: PDEChoice[]) => void;
  onSelectAllPdes?: (event: ChangeEvent<HTMLInputElement>) => void;
  onSelectPde?: (e: SyntheticEvent<HTMLInputElement>) => void;
}

const pdeChoicesName = "pdeChoices";

export const getSelectedPdes = (
  unsavedPdes: PrescriptionDrug[]
): PrescriptionDrug[] => {
  const selectedPdeElements = document.querySelectorAll(
    `input[name=${pdeChoicesName}]:checked`
  );
  const selectedPdes: PrescriptionDrug[] = [];

  selectedPdeElements.forEach(element => {
    const ndc = (element as HTMLInputElement).value;
    unsavedPdes.forEach(pde => {
      if (pde.ndc === ndc) {
        selectedPdes.push(pde);
      }
    });
  });

  return selectedPdes;
};

export interface PDEChoice {
  label: string;
  value: string;
  checked?: boolean;
}

export const PDESelector = ({
  savedPdes,
  unsavedPdes,
  onChoiceUpdated,
  onSelectAllPdes,
  onSelectPde,
}: PDESelectorProps) => {
  const [choices, setChoices] = useState<PDEChoice[]>([]);
  const t = useTranslate();

  useEffect(() => {
    const updatedChoices = unsavedPdes.map(prescription => {
      const label = displayDrug(t, prescription);

      return {
        label,
        value: prescription.ndc,
        checked: false,
      };
    });
    setChoices(updatedChoices);
    if (onChoiceUpdated) {
      onChoiceUpdated(updatedChoices);
    }
  }, []);

  const handleSelectAllPdes = (event: ChangeEvent<HTMLInputElement>): void => {
    const updatedChoices = choices.map(choice => {
      return { ...choice, checked: event.target.checked };
    });

    setChoices(updatedChoices);

    if (onSelectAllPdes) {
      onSelectAllPdes(event);
    }

    if (onChoiceUpdated) {
      onChoiceUpdated(updatedChoices);
    }
  };

  const handleSelectPde = (e: SyntheticEvent<HTMLInputElement>): void => {
    const ndc = e.currentTarget.value;

    const updatedChoices = choices.map(choice => {
      if (choice.value === ndc) {
        return { ...choice, checked: e.currentTarget.checked };
      }
      return choice;
    });

    setChoices(updatedChoices);

    if (onSelectPde) {
      onSelectPde(e);
    }

    if (onChoiceUpdated) {
      onChoiceUpdated(updatedChoices);
    }
  };

  const allSelected = choices.every(choice => choice.checked);

  return (
    <>
      {!!savedPdes.length && (
        <div className="ds-u-margin-top--1 ds-u-margin-bottom--4">
          {t("pde_page.drugs_already_saved")}
          {/* the redundant roles are a fix for a safari VO bug */}
          {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
          <ul className="ds-c-list ds-c-list--bare saved-pdes" role="list">
            {savedPdes.map(pde => {
              return (
                // eslint-disable-next-line jsx-a11y/no-redundant-roles
                <li key={pde.ndc} role="listitem">
                  {displayDrug(t, pde)}
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {!!choices.length && (
        <ChoiceList
          label={<span id="add_drugs">{t("pde_page.add_these_drugs")}</span>}
          hint={
            <Choice
              type="checkbox"
              className="e2e-select-all ds-u-border-bottom--1 ds-u-padding-bottom--1 ds-u-margin-bottom--1 ds-u-measure--narrow"
              onChange={handleSelectAllPdes}
              checked={allSelected}
              value="select-all"
              name="select-all-pdes"
            >
              {t("pde_page.select_all_drugs")}
            </Choice>
          }
          choices={choices}
          className="ds-u-margin-top--2 unsaved-pdes"
          name={pdeChoicesName}
          onChange={handleSelectPde}
          type="checkbox"
        />
      )}
    </>
  );
};
